import type { Workitem } from '@rhythmic/db/types'
import type { WorkitemSearchFilters } from '@rhythmic/types'
import type { UseLiveQueryReturn } from '@tanstack/vue-db'
import type { Hookable } from 'hookable'
import type { Ref } from 'vue'

import type { BacklogContextEvents } from '../useBacklogContext.events.schema'

interface BacklogSearchFiltersChangedLoggerLike {
  debug: (message: string, meta?: Record<string, unknown> | unknown) => void
}

export interface BacklogSearchFiltersChangedHandlerParams {
  hooks: Hookable<{
    [K in keyof BacklogContextEvents]: (event: BacklogContextEvents[K]) => void | Promise<void>
  }>
  logger: BacklogSearchFiltersChangedLoggerLike
  workitemsQuery: Ref<UseLiveQueryReturn<Workitem> | undefined>
  clearBacklogScopedQueries: () => void
  createWorkitemsQuery: (
    teamId: string,
    filters: WorkitemSearchFilters | undefined,
  ) => UseLiveQueryReturn<Workitem> | undefined
  workitemsQueryKey: Ref<string | undefined>
}

function normalizeFiltersKey(filters: WorkitemSearchFilters | undefined): string {
  const statuses = filters?.statuses?.slice().sort().join(',') ?? ''
  const types = filters?.types?.slice().sort().join(',') ?? ''
  return `statuses:${statuses}|types:${types}`
}

/**
 * Registers a hook handler that updates the backlog workitems query when filters change.
 *
 * It clears the query when the team is missing and dedupes by `teamId+filters` so
 * repeated filter emissions do not rebuild the underlying live query.
 */
export default function useBacklogContextOnSearchFiltersChanged({
  hooks,
  logger,
  workitemsQuery,
  clearBacklogScopedQueries,
  createWorkitemsQuery,
  workitemsQueryKey,
}: BacklogSearchFiltersChangedHandlerParams) {
  return hooks.hook('SearchFiltersChanged', (event) => {
    if (!event.teamId) {
      logger.debug('Backlog filters changed but team is missing, clearing backlog-scoped queries', event)
      workitemsQueryKey.value = undefined
      clearBacklogScopedQueries()
      return
    }

    const nextKey = `${event.teamId}:${normalizeFiltersKey(event.filters)}`
    if (workitemsQueryKey.value === nextKey && workitemsQuery.value) return

    logger.debug('Backlog filters changed, creating workitems query', {
      ...event,
      workitemsQueryKey: nextKey,
    })

    workitemsQueryKey.value = nextKey
    workitemsQuery.value = createWorkitemsQuery(event.teamId, event.filters)
  })
}
